// src/pages/MyPolicies.js
import React, { useState, useEffect, useRef } from "react";

const PRIMARY = "#1F2937";

function PolicyViewer({ policy, onClose, onAccepted }) {
  const [accepting,   setAccepting]   = useState(false);
  const [accepted,    setAccepted]    = useState(policy.accepted);
  const [countdown,   setCountdown]   = useState(null);
  const [unlocked,    setUnlocked]    = useState(false);
  const [phase,       setPhase]       = useState("reading"); // reading | questions | disclaimer
  const [qAnswers,    setQAnswers]    = useState({}); // {qId: optionIndex}
  const [qSubmitted,  setQSubmitted]  = useState({}); // {qId: true}
  const [qWrong,      setQWrong]      = useState(false); // failed at least one
  const [viewLogged,  setViewLogged]  = useState(false);
  const timerRef = useRef(null);
  const READ_SECONDS = 10;

  const questions = policy.questions || [];
  const hasQuestions = questions.length > 0 && policy.requireAcceptance;

  function startCountdown() {
    clearInterval(timerRef.current);
    let remaining = READ_SECONDS;
    setCountdown(remaining);
    timerRef.current = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) { clearInterval(timerRef.current); setCountdown(0); setUnlocked(true); }
      else setCountdown(remaining);
    }, 1000);
  }

  function handleIframeLoad() {
    if (accepted || unlocked) return;
    if (!viewLogged) {
      setViewLogged(true);
      fetch(`/api/policies/${policy.id}/view`, { method:"POST" }).catch(()=>{});
    }
    startCountdown();
  }

  function handleReturnToDocument() {
    clearInterval(timerRef.current);
    setPhase("reading");
    setUnlocked(false);
    setQAnswers({});
    setQSubmitted({});
    setQWrong(false);
    fetch(`/api/policies/${policy.id}/view`, { method:"POST" }).catch(()=>{});
    // iframe is already loaded so onLoad won't fire — restart countdown directly
    startCountdown();
  }

  useEffect(() => () => clearInterval(timerRef.current), []);

  function handleReadyToAccept() {
    if (hasQuestions) { setPhase("questions"); setQAnswers({}); setQSubmitted({}); setQWrong(false); }
    else setPhase("disclaimer");
  }

  function handleSubmitQuestions() {
    const allCorrect = questions.every(q => {
      const ans = qAnswers[q.id];
      if (ans === undefined) return false;
      return ans === q.options.findIndex(o => o.correct);
    });
    const submitted = {};
    questions.forEach(q => { submitted[q.id] = true; });
    setQSubmitted(submitted);
    if (allCorrect) setTimeout(() => setPhase("disclaimer"), 600);
    else setQWrong(true);
  }

  async function handleAccept() {
    setAccepting(true);
    try {
      const res = await fetch(`/api/policies/${policy.id}/accept`, { method:"POST" });
      const d   = await res.json();
      if (!res.ok) throw new Error(d.error);
      setAccepted(true);
      setPhase("reading");
      if (onAccepted) onAccepted(policy.id);
      if (onClose) onClose();
    } catch(e) { alert(e.message); }
    finally { setAccepting(false); }
  }

  const allQAnswered = questions.every(q => qAnswers[q.id] !== undefined);
  const canAccept    = unlocked && !accepted && !accepting;

  return (
    <div className="modal-backdrop" style={{ zIndex:200 }}>
      <div className="modal" style={{ maxWidth:820, height:"92vh", display:"flex", flexDirection:"column" }}>
        <div className="modal-header">
          <h2>📄 {policy.name}</h2>
          <button className="btn-ghost" onClick={onClose}><svg viewBox="0 0 20 20" fill="currentColor" width="18"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg></button>
        </div>

        <div style={{ flex:1, overflow:"hidden", background:"#525659" }}>
          <iframe
            src={`/api/policies/${policy.id}/pdf#toolbar=0&navpanes=0`}
            title={policy.name}
            onLoad={handleIframeLoad}
            style={{ width:"100%", height:"100%", border:"none", display:"block" }}
          />
        </div>

        <div style={{ padding:"14px 24px", borderTop:"1px solid var(--gray-200)", background:"#fff",
          display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
          <div style={{ fontSize:13 }}>
            {accepted
              ? <span style={{ color:"#10B981", fontWeight:600 }}>✓ Accepted{policy.acceptedAt ? ` on ${new Date(policy.acceptedAt).toLocaleDateString("en-GB")}` : ""}</span>
              : policy.requireAcceptance && countdown === null
                ? <span style={{ color:"var(--gray-400)" }}>Loading document…</span>
                : policy.requireAcceptance && !unlocked
                  ? <span style={{ color:"#F59E0B" }}>📖 Please read the document — you can accept in {countdown}s</span>
                  : policy.requireAcceptance && !accepted
                    ? <span style={{ color:"#10B981" }}>✓ Ready to accept.</span>
                    : null}
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <button className="btn-secondary" onClick={onClose}>Close</button>
            {policy.requireAcceptance && !accepted && (
              <button className="btn-primary"
                style={{ background: canAccept ? "#1F2937" : "var(--gray-300)",
                  cursor: canAccept ? "pointer" : "not-allowed", transition:"background 0.2s" }}
                onClick={canAccept ? handleReadyToAccept : undefined}
                disabled={!canAccept}>
                {!unlocked && countdown !== null ? `Accept (${countdown}s)` : "✓ Accept this policy"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Questions modal ─────────────────────────────────────────────── */}
      {phase === "questions" && (
        <div className="modal-backdrop" style={{ zIndex:300 }}>
          <div className="modal" style={{ maxWidth:520 }}>
            <div className="modal-header">
              <h2>📝 Understanding Check</h2>
            </div>
            <div className="modal-body" style={{ overflowY:"auto", maxHeight:"70vh" }}>
              <p style={{ fontSize:13, color:"var(--gray-500)", marginBottom:16, lineHeight:1.6 }}>
                Please answer the following question{questions.length>1?"s":""} to confirm you have understood the key points of this policy.
              </p>

              {questions.map((q, qi) => {
                const userAnswer  = qAnswers[q.id];
                const submitted   = !!qSubmitted[q.id];
                const correctIdx  = q.options.findIndex(o => o.correct);
                const wasCorrect  = submitted && userAnswer === correctIdx;
                const wasWrong    = submitted && userAnswer !== correctIdx;
                return (
                  <div key={q.id} style={{ marginBottom:20, padding:"14px 16px", border:"1px solid var(--gray-200)", borderRadius:10,
                    background: wasCorrect ? "#ECFDF5" : wasWrong ? "#FEF2F2" : "#fff" }}>
                    <div style={{ fontWeight:600, fontSize:14, marginBottom:10 }}>
                      {qi+1}. {q.question}
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                      {q.options.filter(o=>o.text.trim()).map((opt, oi) => {
                        let bg = "var(--gray-50)", border = "var(--gray-200)", color = "var(--gray-900)";
                        if (submitted) {
                          if (oi === correctIdx && !wasWrong)  { bg="#ECFDF5"; border="#10B981"; color="#065F46"; }
                          else if (oi===userAnswer&&wasWrong)  { bg="#FEF2F2"; border="#EF4444"; color="#991B1B"; }
                        } else if (userAnswer===oi) { bg="#EEF2FF"; border="#6366F1"; }
                        return (
                          <button key={oi} type="button" disabled={submitted}
                            onClick={() => setQAnswers(a => ({...a,[q.id]:oi}))}
                            style={{ background:bg, border:`1.5px solid ${border}`, color, borderRadius:8,
                              padding:"9px 14px", textAlign:"left", fontSize:13, cursor:submitted?"default":"pointer", transition:"all 0.12s" }}>
                            <span style={{ fontWeight:600, marginRight:8 }}>{String.fromCharCode(65+oi)}.</span>
                            {opt.text}
                            {submitted && wasCorrect && oi===correctIdx && " ✓"}
                            {submitted && oi===userAnswer && wasWrong && " ✗"}
                          </button>
                        );
                      })}
                    </div>
                    {submitted && wasWrong && q.explanation && !qWrong && (
                      <div style={{ marginTop:10, fontSize:13, color:"#991B1B", background:"#FEF2F2", padding:"8px 12px", borderRadius:6 }}>
                        {q.explanation}
                      </div>
                    )}
                  </div>
                );
              })}

              {qWrong && (
                <div style={{ padding:"12px 16px", background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:8, marginBottom:14 }}>
                  <div style={{ fontWeight:600, color:"#991B1B", marginBottom:4 }}>❌ Some answers were incorrect.</div>
                  <div style={{ fontSize:13, color:"#DC2626" }}>
                    Please return to the policy document and re-read it before trying again.
                  </div>
                </div>
              )}

              <div style={{ display:"flex", flexDirection:"column", gap:8, position:"sticky", bottom:0, background:"#fff", paddingTop:12, borderTop:"1px solid var(--gray-100)", marginLeft:-24, marginRight:-24, paddingLeft:24, paddingRight:24 }}>
                {!qWrong ? (
                  <>
                    <button className="btn-primary" style={{ background:"#1F2937" }}
                      onClick={handleSubmitQuestions} disabled={!allQAnswered}>
                      Submit Answers
                    </button>
                    <button className="btn-secondary" onClick={() => setPhase("reading")}>
                      Cancel — return to document
                    </button>
                  </>
                ) : (
                  <button className="btn-primary" style={{ background:"#DC2626" }}
                    onClick={handleReturnToDocument}>
                    ← Return to Document
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Disclaimer modal ────────────────────────────────────────────── */}
      {phase === "disclaimer" && (
        <div className="modal-backdrop" style={{ zIndex:300 }}>
          <div className="modal" style={{ maxWidth:440 }}>
            <div className="modal-header">
              <h2>Confirm Acceptance</h2>
            </div>
            <div className="modal-body">
              <div style={{ fontSize:15, lineHeight:1.7, color:"var(--gray-800)", marginBottom:20,
                padding:"16px 18px", background:"#F0F9FF", border:"1px solid #BAE6FD", borderRadius:8 }}>
                By accepting this policy, you confirm that you have read the contents and agree to comply with this policy at all times.
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:8, position:"sticky", bottom:0, background:"#fff", paddingTop:12, borderTop:"1px solid var(--gray-100)", marginLeft:-24, marginRight:-24, paddingLeft:24, paddingRight:24 }}>
                <button className="btn-primary" style={{ background:"#1F2937" }}
                  onClick={handleAccept} disabled={accepting}>
                  {accepting ? "Recording acceptance…" : "✓ Accept"}
                </button>
                <button className="btn-secondary" onClick={() => setPhase("reading")}>
                  Cancel — return to document
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MyPolicies() {
  const [policies,  setPolicies]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [viewing,   setViewing]   = useState(null);

  async function load() {
    setLoading(true);
    try {
      const d = await fetch("/api/policies-my").then(r=>r.json());
      setPolicies(d.policies || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function handleAccepted(policyId) {
    setPolicies(prev => prev.map(p => p.id === policyId
      ? { ...p, accepted:true, acceptedAt:new Date().toISOString() }
      : p
    ));
  }

  const pending   = policies.filter(p => p.requireAcceptance && !p.accepted);
  const accepted  = policies.filter(p => p.requireAcceptance && p.accepted);
  const viewOnly  = policies.filter(p => !p.requireAcceptance);

  if (loading) return (
    <div style={{ display:"flex", justifyContent:"center", padding:60 }}>
      <span className="spinner" style={{ width:28, height:28 }}/>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">My Policies</div>
          <div className="page-subtitle">Company policies and documents requiring your attention</div>
        </div>
      </div>

      {policies.length === 0 && (
        <div className="card" style={{ padding:40, textAlign:"center", color:"var(--gray-400)" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
          <div>No policies have been published yet.</div>
        </div>
      )}

      {/* Pending acceptance */}
      {pending.length > 0 && (
        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:13, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", color:"#DC2626", marginBottom:10 }}>
            ⚠ Requires Your Acceptance ({pending.length})
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {pending.map(p => (
              <div key={p.id} className="card" style={{ padding:"16px 20px", display:"flex", alignItems:"center", gap:16, borderLeft:"4px solid #DC2626" }}>
                <div style={{ fontSize:28, flexShrink:0 }}>📄</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:15, color:"var(--gray-900)" }}>{p.name}</div>
                  <div style={{ fontSize:12, color:"#DC2626", marginTop:2 }}>Acceptance required</div>
                </div>
                <button className="btn-primary" style={{ background:"#DC2626", flexShrink:0 }}
                  onClick={() => setViewing(p)}>
                  View Policy and Accept
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Accepted */}
      {accepted.length > 0 && (
        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:13, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", color:"#10B981", marginBottom:10 }}>
            Accepted ({accepted.length})
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {accepted.map(p => (
              <div key={p.id} className="card" style={{ padding:"14px 20px", display:"flex", alignItems:"center", gap:16, borderLeft:"4px solid #10B981" }}>
                <div style={{ fontSize:24, flexShrink:0 }}>✅</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:14, color:"var(--gray-900)" }}>{p.name}</div>
                  <div style={{ fontSize:12, color:"var(--gray-400)", marginTop:2 }}>
                    Accepted{p.acceptedAt ? ` on ${new Date(p.acceptedAt).toLocaleDateString("en-GB")}` : ""}
                  </div>
                </div>
                <button className="btn-secondary" style={{ flexShrink:0 }} onClick={() => setViewing(p)}>
                  View Policy
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* View only */}
      {viewOnly.length > 0 && (
        <div>
          <div style={{ fontSize:13, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", color:"var(--gray-400)", marginBottom:10 }}>
            Reference Documents ({viewOnly.length})
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {viewOnly.map(p => (
              <div key={p.id} className="card" style={{ padding:"14px 20px", display:"flex", alignItems:"center", gap:16 }}>
                <div style={{ fontSize:24, flexShrink:0 }}>📋</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:14, color:"var(--gray-900)" }}>{p.name}</div>
                </div>
                <button className="btn-secondary" style={{ flexShrink:0 }} onClick={() => setViewing(p)}>
                  View Policy
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {viewing && (
        <PolicyViewer
          policy={viewing}
          onClose={() => setViewing(null)}
          onAccepted={handleAccepted}
        />
      )}
    </div>
  );
}
